<form name="form" #exportForm="ngForm" class="form-horizontal">
    <div class="mon-dialog-header">
        <mon-page-header header="{{'Export Accessibility Checks' | translate}}"></mon-page-header>
    </div>

    <div class="mon-dialog-body-with-footer">
        <!-- Step 1: Configure Fields -->
        <div class="export-configure" *ngIf="currentStep === 'configure'">
            <div class="mb-4 text-center">
                <h5 translate>Select Fields to Export</h5>
                <p class="text-muted" translate>
                    Choose which fields you want to include in the CSV export. Required fields cannot be deselected.
                </p>
            </div>

            <div class="field-selection-container">
                <div class="field-selection">
                    <div class="form-check mb-3" *ngFor="let field of exportFields">
                        <input 
                            class="form-check-input" 
                            type="checkbox" 
                            [id]="'field-' + field.key"
                            [(ngModel)]="field.selected"
                            [name]="field.key"
                            [disabled]="field.required">
                        <label class="form-check-label" [for]="'field-' + field.key">
                            {{ field.label }}
                            <span class="badge bg-secondary ms-2" *ngIf="field.required" translate>Required</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- Check ID Range Filter -->
            <div class="mt-4">
                <h6 translate>Filter by Check ID Range (Optional)</h6>
                <p class="text-muted small" translate>
                    Specify a range to export only checks within certain Check IDs.
                </p>
                <div class="row">
                    <div class="col-md-12">
                        <div class="form-group">
                            <label for="checkIdFrom" translate>From Check ID</label>
                            <input 
                                type="number" 
                                class="form-control" 
                                id="checkIdFrom"
                                name="checkIdFrom"
                                [(ngModel)]="checkIdFrom"
                                placeholder="e.g., 1"
                                min="0">
                        </div>
                    </div>
                    <div class="col-md-12">
                        <div class="form-group">
                            <label for="checkIdTo" translate>To Check ID</label>
                            <input 
                                type="number" 
                                class="form-control" 
                                id="checkIdTo"
                                name="checkIdTo"
                                [(ngModel)]="checkIdTo"
                                placeholder="e.g., 100"
                                min="0">
                        </div>
                    </div>
                </div>
                <div class="alert alert-warning mt-2" *ngIf="!hasValidCheckIdRange">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    <span translate>"To" value must be greater than or equal to "From" value.</span>
                </div>
            </div>

            <div class="alert alert-info mt-4">
                <i class="fas fa-info-circle me-2"></i>
                <span translate>All accessibility checks will be exported with the selected fields.</span>
            </div>
        </div>

        <!-- Step 2: Progress -->
        <div class="export-progress p-4" *ngIf="currentStep === 'progress'">
            <!-- Progress Header -->
            <div class="progress-header mb-4">
                <div class="row align-items-center">
                    <div class="col-md-32">
                        <h5 translate>Exporting Accessibility Checks</h5>
                        <p class="text-muted mb-0" translate>
                            Please wait while we export all accessibility checks to CSV format.
                        </p>
                    </div>
                    <div class="col-md-16 text-end">
                        <div class="progress-stats">
                            <div class="stat-item">
                                <div class="stat-numbers">
                                    <span class="stat-number">{{ progress.current }}</span>
                                    <span class="stat-divider">/</span>
                                    <span class="stat-total">{{ progress.total }}</span>
                                </div>
                                <div class="stat-label" translate>Checks</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main Progress Bar -->
            <div class="main-progress mb-4">
                <div class="progress" style="height: 30px;">
                    <div 
                        class="progress-bar progress-bar-striped"
                        [ngClass]="[getProgressBarColor(), isCompleted ? '' : 'progress-bar-animated']"
                        role="progressbar" 
                        [style.width.%]="getProgressPercentage()"
                        [attr.aria-valuenow]="progress.current"
                        [attr.aria-valuemin]="0"
                        [attr.aria-valuemax]="progress.total">
                        <span class="fw-bold">{{ getProgressPercentage() | number:'1.0-0' }}%</span>
                    </div>
                </div>
            </div>

            <!-- Status Message -->
            <div class="status-message mb-4">
                <div class="alert" [ngClass]="{'alert-info': !isCompleted, 'alert-success': isCompleted}">
                    <i class="fas" [ngClass]="{'fa-cog fa-spin': !isCompleted, 'fa-check-circle': isCompleted}"></i>
                    <span class="ms-2">{{ getStatusMessage() }}</span>
                </div>
            </div>

            <!-- Error Display -->
            <div class="alert alert-danger" *ngIf="errorMessage">
                <i class="fas fa-exclamation-triangle me-2"></i>
                {{ errorMessage }}
            </div>

            <!-- Progress Statistics -->
            <div class="progress-statistics" *ngIf="isExporting || isCompleted">
                <div class="row">
                    <div class="col-md-24">
                        <div class="stat-card">
                            <div class="stat-icon">
                                <i class="fas fa-clock"></i>
                            </div>
                            <div class="stat-content">
                                <div class="stat-value">{{ formatTime(elapsedTime) }}</div>
                                <div class="stat-label" translate>Elapsed Time</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Dialog Footer -->
        <div class="mon-dialog-footer d-flex justify-content-between">
            <div>
                <!-- Cancel/Back Button -->
                <button
                    type="button"
                    class="btn btn-secondary"
                    *ngIf="currentStep === 'configure'"
                    (click)="cancelDialog()"
                    translate>
                    Cancel
                </button>
                <button
                    type="button"
                    class="btn btn-secondary"
                    *ngIf="currentStep === 'progress' && !isCompleted"
                    [disabled]="!isExporting"
                    (click)="cancel()"
                    translate>
                    Cancel
                </button>
            </div>
            <div>
                <!-- Start Export Button -->
                <button
                    type="button"
                    class="btn btn-primary"
                    *ngIf="currentStep === 'configure'"
                    [disabled]="!canStartExport"
                    (click)="startExportProcess()"
                    translate>
                    Start Export
                </button>
                <!-- Close Button -->
                <button
                    type="button"
                    class="btn btn-primary"
                    *ngIf="currentStep === 'progress' && isCompleted"
                    (click)="close()"
                    translate>
                    Close
                </button>
            </div>
        </div>
    </div>
</form>
